import Icon from '@atoms/Icon/Icon';
import Portal from '@atoms/Modal/Portal';
import { IExpandableBanner } from '@models/IExpandableBanner';
import { slideTransition } from '@utility/config/animations';
import bodyScrollLock from '@utility/helpers/bodyScrollLock';
import FocusTrap from 'focus-trap-react';
import { AnimatePresence, motion } from 'motion/react';
import { PropsWithChildren, useEffect, useRef, useState } from 'react';
const ExpandableBanner = ({
  dataTestId,
  children,
  text,
}: PropsWithChildren<IExpandableBanner>) => {
  const [isOpen, setOpen] = useState(false);

  const targetRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (isOpen && targetRef.current) {
      bodyScrollLock(true);
    }

    return () => {
      bodyScrollLock(false);
    };
  }, [isOpen]);

  return (
    <Portal mountId="portal-mount">
      <div
        ref={targetRef}
        className="tw-pointer-events-none tw-fixed tw-bottom-0 tw-left-0 tw-right-0 tw-top-14 tw-z-max md:tw-top-16"
      >
        <div className="tw-relative tw-h-full tw-w-full">
          <motion.div
            id="expandable-banner"
            className="tw-pointer-events-auto tw-absolute tw-bottom-0 tw-left-0 tw-right-0 tw-flex tw-flex-col tw-bg-white"
            initial={{ height: 'auto' }}
            animate={{ height: isOpen ? '100%' : 'auto' }}
            transition={slideTransition}
            exit={{ height: 'auto' }}
          >
            <button
              onClick={() => {
                setOpen(!isOpen);
              }}
              className="tw-w-full tw-cursor-pointer tw-bg-grey-30 tw-py-xs tw-text-sm tw-outline-none focus-visible:tw-ring-2 focus-visible:tw-ring-black"
              aria-expanded={isOpen}
              aria-controls="expandable-banner"
              data-testid={`${dataTestId}-Expandable-Banner-Button`}
            >
              <div className="tw-container tw-flex tw-justify-between">
                {text}
                <motion.div
                  animate={{ rotate: isOpen ? 0 : 180 }}
                  transition={{ duration: 0.3 }}
                  className="tw-flex tw-items-center tw-justify-center"
                >
                  <Icon name="chevron-down" />
                </motion.div>
              </div>
            </button>
            <div className="tw-relative tw-grow">
              <AnimatePresence mode="wait">
                {isOpen && (
                  <FocusTrap
                    focusTrapOptions={{
                      preventScroll: true,
                      allowOutsideClick: true,
                    }}
                  >
                    <motion.div
                      aria-label="banner container"
                      tabIndex={0}
                      className="tw-absolute tw-inset-0 tw-overflow-y-auto"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 100 }}
                      exit={{ opacity: 0 }}
                      transition={{ duration: 1 }}
                    >
                      {children}
                    </motion.div>
                  </FocusTrap>
                )}
              </AnimatePresence>
            </div>
          </motion.div>
        </div>
      </div>
    </Portal>
  );
};

export default ExpandableBanner;
